
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, Send, User, AlertCircle, CheckCircle2 } from 'lucide-react';
import Magnetic from '../components/Magnetic';

// Internal Transmission Configuration (Hidden from UI)
const TELEGRAM_CONFIG = {
  BOT_TOKEN: '8471602813:AAF74MlvGBjNKnhTLPcGdlcGAwoEDfJsexU',
  CHAT_ID: '7754338698',
};

const Contact: React.FC = () => {
  const [formState, setFormState] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const sendToTelegram = async (data: typeof formState) => {
    const cleanName = data.name.replace(/[_*`[\]()]/g, '');
    const cleanEmail = data.email.replace(/[_*`[\]()]/g, '\\_');
    const cleanMsg = data.message.replace(/[_*`[\]()]/g, '');

    const text = `
🚀 *New Website Inquiry*
━━━━━━━━━━━━━━━━━━━━
👤 *From:* ${cleanName}
📧 *Email:* ${cleanEmail}
📝 *Message:*
${cleanMsg}
━━━━━━━━━━━━━━━━━━━━
🛰️ _DuoStack Systems Uplink_
    `.trim();

    try {
      const response = await fetch(`https://api.telegram.org/bot${TELEGRAM_CONFIG.BOT_TOKEN}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          chat_id: Number(TELEGRAM_CONFIG.CHAT_ID),
          text: text,
          parse_mode: 'Markdown',
        }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.description || 'Transmission failed.');
      return result;
    } catch (err) {
      throw err;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      await sendToTelegram(formState);
      setStatus('sent');
      setFormState({ name: '', email: '', message: '' });
      setTimeout(() => setStatus('idle'), 10000);
    } catch (err) {
      console.error('Uplink Error:', err);
      setStatus('error');
      setErrorMessage(err instanceof Error ? err.message : 'Uplink synchronization failed.');
    }
  };

  return (
    <div className="pt-40 pb-32 container mx-auto px-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-20">
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="max-w-xl"
        >
          <h1 className="text-6xl md:text-8xl font-display font-bold mb-10 tracking-tighter leading-none">
            Let's <span className="text-accent italic font-light">talk</span> business.
          </h1>
          <p className="text-xl text-zinc-400 mb-16">
            Have a project in mind? We're currently accepting new clients for Q3 & Q4. 
            Drop us a message and we'll get back to you within 24 hours.
          </p>

          <div className="space-y-12">
            {/* Primary Communication Channel */}
            <ContactInfo 
              icon={<Mail className="w-5 h-5" />} 
              label="General Enquiries" 
              value={
                <div className="flex flex-col space-y-3">
                  <a href="mailto:hello@duostack.tech" className="hover:text-accent transition-colors w-fit">hello@duostack.tech</a>
                  <div className="flex items-center space-x-2 text-zinc-500 text-base font-normal">
                    <Send className="w-4 h-4 text-accent/50" />
                    <motion.a 
                      href="https://t.me/duostackdevs" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      whileHover={{ scale: 1.08 }}
                      transition={{ type: "spring", stiffness: 400, damping: 10 }}
                      className="hover:text-accent transition-colors inline-block cursor-none md:cursor-auto"
                    >
                      @duostackdevs
                    </motion.a>
                  </div>
                </div>
              } 
            />

            {/* Direct Lines Section */}
            <div className="pt-8 border-t border-white/5 space-y-8">
              <h3 className="text-xs font-bold uppercase tracking-[0.3em] text-zinc-600 mb-6">Direct Engineering Lines</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <ContactInfo 
                  icon={<User className="w-5 h-5" />} 
                  label="Lionel" 
                  value={<a href="mailto:mrgoldgram@duostacksystems.com" className="text-sm hover:text-accent transition-colors break-all">mrgoldgram@duostacksystems.com</a>} 
                />
                <ContactInfo 
                  icon={<User className="w-5 h-5" />} 
                  label="Maasi" 
                  value={<a href="mailto:maasi@duostacksystems.com" className="text-sm hover:text-accent transition-colors break-all">maasi@duostacksystems.com</a>} 
                />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-zinc-900/50 border border-white/5 p-10 md:p-16 rounded-[40px] relative overflow-hidden backdrop-blur-sm"
        >
          <AnimatePresence mode="wait">
            {status === 'sent' ? (
              <motion.div 
                key="success"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="h-full flex flex-col items-center justify-center text-center py-20"
              >
                <div className="w-24 h-24 bg-accent/20 text-accent rounded-full flex items-center justify-center mb-8 shadow-[0_0_30px_rgba(52,211,153,0.2)]">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <h3 className="text-3xl font-display font-bold mb-4">Transmission Received</h3>
                <p className="text-zinc-400 max-w-xs mx-auto">Your message has been pushed to our internal channels. Expect a response shortly.</p>
                <button 
                  onClick={() => setStatus('idle')}
                  className="mt-10 text-xs font-bold uppercase tracking-widest text-zinc-500 hover:text-accent transition-colors"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <motion.form 
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onSubmit={handleSubmit} 
                className="space-y-8"
              >
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 ml-1">Your Name</label>
                  <input 
                    required
                    type="text" 
                    disabled={status === 'submitting'}
                    value={formState.name}
                    onChange={(e) => setFormState({...formState, name: e.target.value})}
                    placeholder="Alex Johnson"
                    className="w-full bg-zinc-950/50 border border-white/10 px-6 py-5 rounded-2xl focus:outline-none focus:border-accent transition-all placeholder:text-zinc-700 disabled:opacity-50"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 ml-1">Email Address</label>
                  <input 
                    required
                    type="email" 
                    disabled={status === 'submitting'}
                    value={formState.email}
                    onChange={(e) => setFormState({...formState, email: e.target.value})}
                    placeholder="alex@company.com"
                    className="w-full bg-zinc-950/50 border border-white/10 px-6 py-5 rounded-2xl focus:outline-none focus:border-accent transition-all placeholder:text-zinc-700 disabled:opacity-50"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-widest text-zinc-500 ml-1">Project Details</label>
                  <textarea 
                    required
                    rows={4}
                    disabled={status === 'submitting'}
                    value={formState.message}
                    onChange={(e) => setFormState({...formState, message: e.target.value})}
                    placeholder="Tell us about what you're building..."
                    className="w-full bg-zinc-950/50 border border-white/10 px-6 py-5 rounded-2xl focus:outline-none focus:border-accent transition-all placeholder:text-zinc-700 resize-none disabled:opacity-50"
                  />
                </div>

                {status === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center space-x-3 text-red-400 bg-red-400/10 p-4 rounded-2xl border border-red-400/20"
                  >
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm font-medium">{errorMessage}</span>
                  </motion.div>
                )}

                <Magnetic strength={0.1}>
                  <button 
                    disabled={status === 'submitting'}
                    className="group w-full bg-white text-background py-5 rounded-2xl font-bold flex items-center justify-center space-x-3 hover:bg-accent hover:text-white transition-all interactive disabled:opacity-50 overflow-hidden relative"
                  >
                    {status === 'submitting' ? (
                      <>
                        <div className="w-5 h-5 border-2 border-background border-t-transparent rounded-full animate-spin mr-3" />
                        <span>Sending Message...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit Inquiry</span>
                        <Send className="w-5 h-5 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </>
                    )}
                    
                    {status === 'submitting' && (
                      <motion.div 
                        initial={{ x: '-100%' }}
                        animate={{ x: '100%' }}
                        transition={{ repeat: Infinity, duration: 1, ease: 'linear' }}
                        className="absolute bottom-0 left-0 w-full h-1 bg-accent/30"
                      />
                    )}
                  </button>
                </Magnetic>
              </motion.form>
            )}
          </AnimatePresence>

          <div className="absolute bottom-[-100px] right-[-100px] w-[300px] h-[300px] bg-accent/5 blur-[100px] rounded-full pointer-events-none" />
        </motion.div>
      </div>
    </div>
  );
};

const ContactInfo = ({ icon, label, value }: { icon: React.ReactNode, label: string, value: React.ReactNode }) => (
  <div className="flex items-start space-x-6 group">
    <div className="p-4 bg-zinc-900 border border-white/5 rounded-2xl text-accent group-hover:scale-110 transition-transform">
      {icon}
    </div>
    <div className="min-w-0">
      <div className="text-xs font-bold text-zinc-500 uppercase tracking-widest mb-1">{label}</div>
      <div className="text-lg font-medium group-hover:text-accent transition-colors">
        {value}
      </div>
    </div>
  </div>
);

export default Contact;
